/**
 * cart-drawer.js — <cart-drawer> Web Component
 * Slide-in cart panel with optimistic updates, focus trap, and Cart API integration.
 */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this._panel = this.querySelector('.cart-drawer__panel');
    this._overlay = this.querySelector('.cart-drawer__overlay');
    this._trigger = null;
    this._releaseFocus = null;
    this._isUpdating = false;

    document.addEventListener(Summit.events.CART_OPEN, this._open.bind(this));
    document.addEventListener(Summit.events.CART_CLOSE, this._close.bind(this));
    document.addEventListener(Summit.events.CART_ADD, this._onCartAdd.bind(this));
    document.addEventListener(Summit.events.QUANTITY_CHANGE, this._onQuantityChange.bind(this));

    this._overlay?.addEventListener('click', this._close.bind(this));

    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-cart-close]');
      if (closeBtn) this._close();

      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) this._removeItem(removeBtn);

      const checkoutBtn = e.target.closest('[data-cart-checkout]');
      if (checkoutBtn) this._checkout();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen()) this._close();
    });
  }

  _isOpen() {
    return document.body.classList.contains('cart-drawer-open');
  }

  _open(event) {
    this._trigger = event.detail?.trigger || document.querySelector('[data-cart-trigger]');
    document.body.classList.add('cart-drawer-open');
    this._panel?.classList.add('cart-drawer__panel--open');

    if (window.SummitModal && this._panel) {
      this._releaseFocus = window.SummitModal.trapFocus(this._panel);
    }

    this._fetchAndRender();
  }

  _close() {
    document.body.classList.remove('cart-drawer-open');
    this._panel?.classList.remove('cart-drawer__panel--open');

    if (typeof this._releaseFocus === 'function') {
      this._releaseFocus();
      this._releaseFocus = null;
    }
    if (this._trigger) {
      window.SummitModal?.releaseFocus(this._trigger);
      this._trigger = null;
    }
  }

  async _fetchAndRender() {
    try {
      const cart = await Summit.cartAPI.get();
      this._renderItems(cart);
    } catch (err) {
      Summit.toast.show(Summit.locales?.cart?.error_generic || 'Something went wrong.', 'error');
    }
  }

  _renderItems(cart) {
    const itemsEl = this.querySelector('[data-cart-items]');
    const totalEl = this.querySelector('[data-cart-total]');
    const emptyEl = this.querySelector('[data-cart-empty]');
    const filledEl = this.querySelector('[data-cart-filled]');
    const countEl = this.querySelector('[data-cart-item-count]');

    if (countEl) countEl.textContent = cart.item_count;

    if (cart.item_count === 0) {
      emptyEl?.removeAttribute('hidden');
      filledEl?.setAttribute('hidden', '');
      if (itemsEl) itemsEl.innerHTML = '';
    } else {
      emptyEl?.setAttribute('hidden', '');
      filledEl?.removeAttribute('hidden');
      if (itemsEl) itemsEl.innerHTML = cart.items.map((item) => this._itemHTML(item)).join('');
    }

    if (totalEl) {
      totalEl.textContent = Summit.formatMoney(cart.total_price);
    }

    Summit.dispatch(Summit.events.CART_RENDERED, { cart });
  }

  _itemHTML(item) {
    const imgUrl = item.image ? item.image.replace('.jpg', '_120x120.jpg') : '';
    const price = Summit.formatMoney(item.final_line_price);
    const unitPrice = Summit.formatMoney(item.final_price);
    const titleEscaped = item.product_title.replace(/"/g, '&quot;');
    const trashIcon = `<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>`;
    return `
      <div class="cart-item" data-line-item data-key="${item.key}" data-variant-id="${item.variant_id}">
        ${imgUrl ? `<img class="cart-item__image" src="${imgUrl}" alt="${item.title.replace(/"/g, '&quot;')}" width="80" height="80" loading="lazy">` : ''}
        <div class="cart-item__body">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="cart-item__unit-price">${unitPrice} each</p>
          <div class="cart-item__footer">
            <quantity-input
              class="cart-item__qty"
              data-key="${item.key}"
              min="0"
              max="999"
              value="${item.quantity}"
            >
              <button class="qty-btn" data-action="decrement" aria-label="Decrease quantity">−</button>
              <input type="number" class="qty-input" value="${item.quantity}" min="0" max="999" aria-label="Quantity for ${item.title.replace(/"/g, '&quot;')}">
              <button class="qty-btn" data-action="increment" aria-label="Increase quantity">+</button>
            </quantity-input>
            <span class="cart-item__line-price">${price}</span>
          </div>
        </div>
        <button
          class="cart-item__remove"
          data-cart-remove
          data-key="${item.key}"
          aria-label="Remove ${titleEscaped} from cart"
        >${trashIcon}</button>
      </div>`;
  }

  async _onCartAdd(event) {
    const { variantId, quantity = 1, properties = {} } = event.detail || {};
    if (!variantId) return;

    const btn = event.detail?.trigger;
    if (btn) { btn.classList.add('btn--loading'); btn.setAttribute('aria-disabled', 'true'); }

    try {
      await Summit.cartAPI.add({ id: variantId, quantity, properties });
      // No auto-open: bubble appears via cart:updated; drawer opens only on explicit user action
    } catch (err) {
      Summit.toast.show(err.message || 'Could not add to cart.', 'error');
    } finally {
      if (btn) { btn.classList.remove('btn--loading'); btn.removeAttribute('aria-disabled'); }
    }
  }

  async _onQuantityChange(event) {
    if (this._isUpdating) return;
    const { value, input } = event.detail || {};
    const lineItem = input?.closest('[data-line-item]');
    if (!lineItem) return;

    const key = lineItem.dataset.key;
    if (!key) return;

    this._isUpdating = true;
    try {
      await Summit.cartAPI.change({ id: key, quantity: parseInt(value, 10) });
      this._fetchAndRender();
    } catch (err) {
      Summit.toast.show(err.message || 'Could not update cart.', 'error');
      this._fetchAndRender();
    } finally {
      this._isUpdating = false;
    }
  }

  async _removeItem(btn) {
    const key = btn.dataset.key;
    if (!key) return;

    const lineItem = btn.closest('[data-line-item]');
    if (lineItem) { lineItem.style.opacity = '0.4'; lineItem.style.pointerEvents = 'none'; }

    try {
      await Summit.cartAPI.change({ id: key, quantity: 0 });
      this._fetchAndRender();
    } catch (err) {
      if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
      Summit.toast.show(err.message || 'Could not remove item.', 'error');
    }
  }

  _checkout() {
    if (!Summit.cart || Summit.cart.item_count === 0) return;
    window.location.href = '/checkout';
  }
}

customElements.define('cart-drawer', CartDrawer);
